<template>
  <div class="calendar-block__container" v-if="!appError">
    <div v-if="isLoaded" class="calendar" :class="{'calendar--hidden': !showContent}" >
      <div v-if="!isMiniCalendar" class="calendar__filter filter">
        <ul v-if="filterTags" class="filter__list" :class="filterArray.length === 0 ? 'clear-btn-exists' : ''">
          <FilterTag
            v-for="tag in filterTags"
            :key="tag.TagId"
            :tag="tag"
            :isActive="filterArray.includes(tag.TagId)"
            @onFilterClick="onFilterClick"
          />
        </ul>
        <button v-if="filterArray.length > 0" @click="resetFilter()" class="filter__clear-button link link--clear">Rensa filter</button>
      </div>
      <CalendarList
        :filteredCalendarEvents="displayedCalendarEvents"
        :translations="translations"
        :culture="culture"
        :isMiniCalendar="isMiniCalendar"
        :itemDisplayOption="effectiveItemDisplayOption"
        :isStartPage="isStartPage"
        :focusEventIndex="focusEventIndex"
      />
      <div v-if="showMoreAvailable" class="calendar-block__show-more show-more">
        <button type="button" class="calendar-block__show-more-button button button--secondary" @click="showMore">
          <span class="button__icon icon icon--plus"></span>
          <span>{{ showMoreText }}</span>
        </button>
      </div>
    </div>
    <component v-else :is="activeSpinner" />
  </div>

  <section class="calendar__error-message no-content" v-else>
    <p>{{ errorMessage }}</p>
  </section>
  
</template>

<script>
import * as calendarService from "./services/calendar-service";
import FilterTag from "./components/filter-tag.vue";
import CalendarList from "./components/calendar-list.vue";
import SpinnerJvm from '@shared/spinner/spinner-jvm.vue';
import SpinnerMm from '@shared/spinner/spinner-mm.vue';
import SpinnerVrak from '@shared/spinner/spinner-vrak.vue';

const spinnerMap = {
  jvm: SpinnerJvm,
  mm: SpinnerMm,
  vrak: SpinnerVrak,
};

export default {
  name: "app",
  props: {
    configuration: {
      type: Object,
      default: null,
    },
  },
  components: {
    SpinnerJvm,
    SpinnerMm,
    SpinnerVrak,
    FilterTag: FilterTag,
    CalendarList: CalendarList,
  },
  data() {
    return {
      activeFilters: [],
      calendarEvents: [],
      filterTags: [],
      translations: {},
      culture: "",
      isLoaded: false,
      appError: false,
      errorMessage: "",
      filterArray: [],
      showContent: false,
      isMiniCalendar: false,
      siteIdentifier: '',
      numberOfItems: 0,
      itemDisplayOption: '',
      isStartPage: false,
      visibleEventCount: 0,
      focusEventIndex: null,
    };
  },
  mounted: function () {
    this.isMiniCalendar = this.configuration?.miniCalendar ?? this.getElementValue("miniCalendar");
    this.siteIdentifier = this.configuration?.siteIdentifier ?? this.getElementValueAsString("siteIdentifier") ?? 'jvm';
    this.numberOfItems = this.configuration?.numberOfItems ?? 0;
    this.itemDisplayOption = this.configuration?.itemDisplayOption ?? '';
    this.isStartPage = this.configuration?.isStartPage ?? false;
    this.visibleEventCount = this.initialVisibleEventCount;

    calendarService.getCalendarData(this.configuration)
        .then((data) => {
          this.calendarEvents = data.SingleEvents;
          this.filterTags = data.CompleteTags;
          this.translations = data.Translations;
          this.culture = data.Culture;

          // Hide filter heading if no tags are available
          const filterHeading = document.querySelector('.calendar-block__filter-heading');

          if (!this.filterTags.length && filterHeading) {
            filterHeading.style.display = 'none';
          }

          if (this.calendarEvents.length === 0) {
            this.appError = true;
            this.errorMessage = this.translations.ErrorMessageNoCalendars;
          }

          // Set isLoaded to true after 1 second to prevent spinner from flashing
          setTimeout(() => {
            this.isLoaded = true;
            if (filterHeading) {
              filterHeading.hidden = this.isMiniCalendar;
            }
          }, 1000);
        })
        .catch(() => {
          this.appError = true;
          this.errorMessage = this?.translations?.ErrorMessageFailed;
        });
  },
  computed: {
    activeSpinner() {
      return spinnerMap[this.siteIdentifier];
    },
    filterState() {
      return this.activeFilters.length > 0 ? "button--selected" : "";
    },
    filteredCalendarEvents: function () {
      let filter = this.filterArray;

      // If no filters are selected, return all calendar items
      if (filter.length === 0) {
        return this.getGroupedCalendarEvents(this.calendarEvents);
      }
      
      // If any of the tags in the filter array is included in the calendar item, include the item
      return this.getGroupedCalendarEvents(this.calendarEvents.filter(calendar => {
        return calendar.CompleteTags.some(tag => filter.includes(tag.TagId));
      }));

    },
    displayedCalendarEvents() {
      if (!this.isMiniCalendar) {
        return this.filteredCalendarEvents;
      }

      const monthKey = Object.keys(this.filteredCalendarEvents)[0];
      if (!monthKey) {
        return {};
      }

      return {
        [monthKey]: this.getUniqueCalendarEvents(this.filteredCalendarEvents[monthKey])
          .slice(0, this.visibleEventCount),
      };
    },
    totalMiniEventCount() {
      if (!this.isMiniCalendar) {
        return 0;
      }

      return Object.values(this.filteredCalendarEvents)
        .flatMap(events => events)
        .filter((event, index, events) => events.findIndex(candidate => candidate.CalendarItemPageReference === event.CalendarItemPageReference) === index)
        .length;
    },
    initialVisibleEventCount() {
      if (this.isStartPage) {
        return 3;
      }

      return this.numberOfItems > 0 ? this.numberOfItems : 12;
    },
    showMoreAvailable() {
      return this.isMiniCalendar && !this.isStartPage && this.numberOfItems === 0 && this.totalMiniEventCount > this.visibleEventCount;
    },
    remainingMiniEventCount() {
      return Math.max(0, this.totalMiniEventCount - this.visibleEventCount);
    },
    showMoreText() {
      return `${this.configuration.showMoreLabel} (${this.remainingMiniEventCount})`;
    },
    effectiveItemDisplayOption() {
      if (!this.isStartPage) {
        return this.itemDisplayOption;
      }

      return this.totalMiniEventCount <= 2 ? 'Halva' : 'Tredjedel';
    },
  },
  methods: {
    onFilterClick(filterTag) {
      if (!this.filterArray.includes(filterTag)) {
        this.filterArray.push(filterTag);
      } else {
        this.filterArray = this.filterArray.filter(tag => tag !== filterTag);
      }
    },
    resetFilter() {
      this.filterArray = [];
    },
    getElementValue(id) {
      const element = document.getElementById(id);
      return element ? element.value === "True" : false;
    },
    getElementValueAsString(id) {
      const element = document.getElementById(id);
      return element?.value;
    },
    showMore() {
      this.focusEventIndex = this.visibleEventCount;
      this.visibleEventCount += 12;
    },
    getUniqueCalendarEvents(calendarItems) {
      const seen = new Set();
      return calendarItems.filter(item => {
        if (seen.has(item.CalendarItemPageReference)) {
          return false;
        }
        seen.add(item.CalendarItemPageReference);
        return true;
      });
    },
    getGroupedCalendarEvents: function (calendarList) {
      // If isMiniCalendar is true, return all calendar items in the current month. Always only three items. 
      if(this.isMiniCalendar) {
        const currentMonthName = new Date().toLocaleString(this.culture, { month: 'long' });

        return {
          [currentMonthName]: calendarList
        };
      }

      // Group events (and split recurring events) by year and month from the current month onward.
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const groupedCalendarEvents = calendarList.reduce((group, calenderEvent) => {

        const {Start, PeriodEndDate, IsRecurring} = calenderEvent;
        const startDate = new Date(Start);
        const periodEndDate = new Date(PeriodEndDate);

        if (IsRecurring) {
          const firstVisibleDate = startDate > currentMonthStart ? startDate : currentMonthStart;
          const firstVisibleMonth = new Date(firstVisibleDate.getFullYear(), firstVisibleDate.getMonth(), 1);
          const endMonth = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), 1);

          for (let currentDate = firstVisibleMonth; currentDate <= endMonth; currentDate.setMonth(currentDate.getMonth() + 1)) {
            const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            group[monthKey] = group[monthKey] || [];
            let calendarEventCopy = Object.assign({}, calenderEvent);
            group[monthKey].push(calendarEventCopy);
          }
        } else {
          const visibleDate = startDate > currentMonthStart ? startDate : currentMonthStart;
          const monthKey = `${visibleDate.getFullYear()}-${String(visibleDate.getMonth() + 1).padStart(2, '0')}`;
          group[monthKey] = group[monthKey] || [];
          group[monthKey].push(calenderEvent);
        }
        return group;
      }, {});

      Object.values(groupedCalendarEvents).forEach((monthEvents) => {
        monthEvents.sort((a, b) => {
          return (a.IsRecurring === b.IsRecurring) ? 0 : a.IsRecurring ? -1 : 1;
        });
      });

      return groupedCalendarEvents;
    }
  },
  watch: {
    isLoaded: {
      handler(newValue) {
        setTimeout(() => {
          this.showContent = newValue;
        });
      },
      deep: true
    }
  }
  
};
</script>
